import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ExternalLink } from "lucide-react";
import axios from "axios";
import { api, errorText, SERVICES } from "@/lib/api";
import { Field, Input, Area, Select, Btn, Card, PageHead, Spinner } from "./ui";

const NEW_ITEM = { name_no: "", name_en: "", price: "", service: "annet" };
const move = (arr, i) => (i === 0 ? arr : [...arr.slice(0, i - 1), arr[i], arr[i - 1], ...arr.slice(i + 1)]);

const ItemRow = ({ it, onChange, onRemove, onUp, testid }) => (
  <div className="grid gap-2 border-b border-white/10 py-3 md:grid-cols-[1.4fr_1.2fr_0.9fr_1fr_auto]" data-testid={testid}>
    <Input value={it.name_no} onChange={(e) => onChange({ name_no: e.target.value })} placeholder="Tjeneste (norsk)" data-testid={`${testid}-name-no`} />
    <Input value={it.name_en} onChange={(e) => onChange({ name_en: e.target.value })} placeholder="Service (English)" data-testid={`${testid}-name-en`} />
    <Input value={it.price} onChange={(e) => onChange({ price: e.target.value })} placeholder="Fra 2 490 kr" data-testid={`${testid}-price`} />
    <Select value={it.service} onChange={(e) => onChange({ service: e.target.value })} options={SERVICES.map((s) => [s.value, s.label])} data-testid={`${testid}-service`} />
    <div className="flex gap-1">
      <Btn onClick={onUp} aria-label="Flytt opp" title="Flytt opp"><ArrowUp size={14} /></Btn>
      <Btn onClick={onRemove} aria-label="Fjern" title="Fjern" data-testid={`${testid}-remove`}><Trash2 size={14} /></Btn>
    </div>
  </div>
);

export const Prices = () => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/prices`).then((r) => setP(r.data)).catch((e) => toast.error(errorText(e))); }, []);
  if (!p) return <Spinner />;
  const setCat = (ci, patch) => setP({ ...p, categories: p.categories.map((c, i) => (i === ci ? { ...c, ...patch } : c)) });
  const setItem = (ci, ii, patch) => setCat(ci, { items: p.categories[ci].items.map((x, j) => (j === ii ? { ...x, ...patch } : x)) });
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/prices", p); setP(r.data); toast.success("Prislisten er lagret og vises på /priser"); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="prices-admin">
      <PageHead title="Priser" sub="Prislisten på rbtun.no/priser. «Tjeneste» styrer hvilken tjeneste som forhåndsvelges når kunden trykker «Få tilbud»." />
      <div className="space-y-6">
        {p.categories.map((c, ci) => (
          <Card key={ci} data-testid={`prices-cat-${ci}`}>
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <Field label="Kategori (norsk)"><Input value={c.title_no} onChange={(e) => setCat(ci, { title_no: e.target.value })} data-testid={`prices-cat-${ci}-title-no`} /></Field>
              <Field label="Category (English)"><Input value={c.title_en} onChange={(e) => setCat(ci, { title_en: e.target.value })} data-testid={`prices-cat-${ci}-title-en`} /></Field>
              <div className="flex items-end gap-1">
                <Btn onClick={() => setP({ ...p, categories: move(p.categories, ci) })} title="Flytt opp"><ArrowUp size={14} /></Btn>
                <Btn onClick={() => setP({ ...p, categories: p.categories.filter((_, i) => i !== ci) })} title="Fjern kategori" data-testid={`prices-cat-${ci}-remove`}><Trash2 size={14} /></Btn>
              </div>
            </div>
            <div className="mt-3">
              {c.items.map((it, ii) => (
                <ItemRow key={ii} it={it} testid={`prices-item-${ci}-${ii}`} onChange={(patch) => setItem(ci, ii, patch)}
                  onRemove={() => setCat(ci, { items: c.items.filter((_, j) => j !== ii) })} onUp={() => setCat(ci, { items: move(c.items, ii) })} />
              ))}
            </div>
            <Btn className="mt-3" onClick={() => setCat(ci, { items: [...c.items, { ...NEW_ITEM }] })} data-testid={`prices-cat-${ci}-add`}><Plus size={14} /> Ny tjeneste</Btn>
          </Card>
        ))}
        <Btn onClick={() => setP({ ...p, categories: [...p.categories, { title_no: "", title_en: "", items: [{ ...NEW_ITEM }] }] })} data-testid="prices-add-cat"><Plus size={14} /> Ny kategori</Btn>
        <Card className="grid gap-3 md:grid-cols-2">
          <Field label="Viktig-tekst (norsk)"><Area rows={4} value={p.note_no} onChange={(e) => setP({ ...p, note_no: e.target.value })} data-testid="prices-note-no" /></Field>
          <Field label="Important text (English)"><Area rows={4} value={p.note_en} onChange={(e) => setP({ ...p, note_en: e.target.value })} data-testid="prices-note-en" /></Field>
        </Card>
        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-white/10 bg-[#08090b] py-4">
          <Btn variant="red" onClick={save} disabled={busy} data-testid="prices-save">Lagre prisliste</Btn>
          <a href="/priser" target="_blank" rel="noopener noreferrer" data-testid="prices-view"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-zinc-200 hover:border-white/40"><ExternalLink size={14} /> Vis siden</a>
        </div>
      </div>
    </div>
  );
};
